import {
	createTableHook,
	type RowData,
	type TableOptions,
	type TableState,
} from "@tanstack/react-table";
import {
	appCellContext,
	appHeaderContext,
	appTableContext,
} from "./app-table-contexts";
import { appTableFeatures } from "./app-table-features";
import {
	appTableCellComponents,
	appTableHeaderComponents,
	appTableShellComponents,
} from "./app-table-ui";
import { useRegisterAppTableDevtools } from "./use-app-table-devtools";

const appTable = createTableHook({
	features: appTableFeatures,
	tableContext: appTableContext,
	cellContext: appCellContext,
	headerContext: appHeaderContext,
	tableComponents: appTableShellComponents,
	cellComponents: appTableCellComponents,
	headerComponents: appTableHeaderComponents,
	/** Match CustomTableWrapper deferred-resize convention. */
	columnResizeMode: "onEnd",
	/**
	 * Pin the sort cycle to unsorted → asc → desc → unsorted.
	 * Without this, TanStack infers `firstSortDir` from sampled cell values via
	 * `getAutoSortDir`. Sparse columns (Description, Sub-Organization, etc.) often
	 * sample only nulls after a server desc sort, flipping firstDir to `desc` and
	 * skipping the clear/unsorted step so the header only toggles asc ↔ desc.
	 */
	sortDescFirst: false,
});

export const createAppColumnHelper = appTable.createAppColumnHelper;
export const useTableContext = appTable.useTableContext;
export const useCellContext = appTable.useCellContext;
export const useHeaderContext = appTable.useHeaderContext;
export const appFeatures = appTable.appFeatures;

type UseAppTableOptions<TData extends RowData> = Omit<
	TableOptions<typeof appTableFeatures, TData>,
	"features"
>;

/**
 * App-wide TanStack Table v9 hook with shared features, Destiny cell/header
 * components, defaults, and Devtools registration.
 *
 * Pass a stable unique `key` for Devtools identification.
 *
 * Registered cell components (via `table.AppCell` / `useCellContext`):
 * `SecondaryTextCell`, `PrimaryTextCell`, `DateCell`, `BadgeCell`, etc.
 *
 * @example
 * ```tsx
 * const helper = createAppColumnHelper<Person>()
 * const columns = helper.columns([
 *   helper.accessor('name', {
 *     header: 'Name',
 *     cell: () => <PrimaryTextCell />,
 *   }),
 * ])
 *
 * // In the row render loop — AppCell must wrap content *inside* Aria Cell
 * // (React Aria Collection can re-parent Cells and drop an outer provider):
 * <CustomTable.Cell>
 *   <table.AppCell cell={cell}>
 *     {() => <table.FlexRender cell={cell} />}
 *   </table.AppCell>
 * </CustomTable.Cell>
 * ```
 */
export function useAppTable<
	TData extends RowData,
	TSelected = TableState<typeof appTableFeatures>,
>(
	tableOptions: UseAppTableOptions<TData>,
	selector?: (state: TableState<typeof appTableFeatures>) => TSelected,
) {
	const table = appTable.useAppTable(tableOptions, selector);
	useRegisterAppTableDevtools(table);
	return table;
}

export type { UseAppTableOptions };
export type AppReactTable = ReturnType<typeof useAppTable>;
